import React, { useCallback, useEffect, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Content, Button } from "adminlte-2-react";
import {
	getPage,
	createPageWidget,
	deletePageWidget,
	batchUpdateLayout,
} from "../../api/pages";
import { API } from "../../constant";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import WidgetRenderer from "../../widgets/WidgetRenderer";

const ResponsiveGridLayout = WidthProvider(Responsive);

const BREAKPOINTS = { lg: 1200, md: 996, sm: 768 };
const COLS = { lg: 12, md: 8, sm: 4 };
const ROW_HEIGHT = 60;
const DEFAULT_WIDGET_SIZE = { w: 6, h: 6 };

/**
 * Следует легаси-паттерну роутинга проекта (см. Main.jsx: props.match.params.id).
 * В App.jsx подключается как <PageCanvas path="/pages/:id" />.
 */
function PageCanvas(props) {
	const pageId = props?.match?.params?.id;

	const [page, setPage] = useState(null);
	const [layout, setLayout] = useState([]);
	const [editMode, setEditMode] = useState(false);
	const [units, setUnits] = useState([]);
	const [selectedUnitId, setSelectedUnitId] = useState("");
	const [error, setError] = useState(null);

	// Снимок последнего СОХРАНЁННОГО в БД layout — по нему считаем diff,
	// чтобы в batch-запрос улетали только реально изменившиеся виджеты.
	const lastSavedLayoutRef = useRef({});

	const loadPage = useCallback(async () => {
		const data = await getPage(pageId);
		setPage(data);

		const nextLayout = (data?.widgets ?? []).map((w) => ({
			i: String(w.id),
			x: w.x,
			y: w.y,
			w: w.w,
			h: w.h,
		}));

		setLayout(nextLayout);
		lastSavedLayoutRef.current = Object.fromEntries(
			nextLayout.map((item) => [item.i, item]),
		);
	}, [pageId]);

	useEffect(() => {
		loadPage();
	}, [loadPage]);

	// Список юнитов для селекта "добавить виджет" — тот же запрос,
	// что уже используется в App.jsx (fetchMenu), без отдельного api-модуля.
	useEffect(() => {
		(async () => {
			try {
				const res = await fetchWithAuth(`${API}/units`);
				const json = await res.json();
				setUnits(
					(json.data ?? []).map((item) => ({
						id: item.id,
						...item.attributes,
					})),
				);
			} catch (e) {
				console.error(e);
			}
		})();
	}, []);

	const persistLayout = useCallback(
		async (nextLayout) => {
			const changed = nextLayout.filter((item) => {
				const prev = lastSavedLayoutRef.current[item.i];
				return (
					!prev ||
					prev.x !== item.x ||
					prev.y !== item.y ||
					prev.w !== item.w ||
					prev.h !== item.h
				);
			});

			if (changed.length === 0) return;

			try {
				await batchUpdateLayout(
					changed.map(({ i, x, y, w, h }) => ({
						id: Number(i),
						x,
						y,
						w,
						h,
					})),
				);
				lastSavedLayoutRef.current = {
					...lastSavedLayoutRef.current,
					...Object.fromEntries(changed.map((item) => [item.i, item])),
				};
			} catch (e) {
				setError(e.message);
				// сервер отказал валидацию (например, w/h <= 0) — откатываем
				// локальный layout к тому, что реально сохранено в БД
				loadPage();
			}
		},
		[loadPage],
	);

	// onDragStop/onResizeStop, а не onLayoutChange — фиксируем состояние
	// только по окончании жеста, не на каждый промежуточный кадр.
	const handleLayoutSettled = (nextLayout) => {
		setLayout(nextLayout);
		persistLayout(nextLayout);
	};

	const handleAddWidget = async () => {
		if (!selectedUnitId) return;

		const occupiedRows = layout.reduce(
			(max, item) => Math.max(max, item.y + item.h),
			0,
		);

		try {
			await createPageWidget({
				pageId,
				unitId: selectedUnitId,
				x: 0,
				y: occupiedRows, // новый виджет всегда встаёт под остальными
				...DEFAULT_WIDGET_SIZE,
			});
			setSelectedUnitId("");
			await loadPage();
		} catch (e) {
			setError(e.message);
		}
	};

	const handleRemoveWidget = async (widgetId) => {
		try {
			await deletePageWidget(widgetId);
			await loadPage();
		} catch (e) {
			setError(e.message);
		}
	};

	if (!page) {
		return <Content title="Загрузка...">{null}</Content>;
	}

	const usedUnitIds = new Set(
		(page.widgets ?? []).map((w) => w.unit?.id).filter(Boolean),
	);
	const availableUnits = units.filter((u) => !usedUnitIds.has(u.id));

	return (
		<Content title={page.name}>
			{error && (
				<div className="alert alert-danger" onClick={() => setError(null)}>
					{error}
				</div>
			)}

			<div
				style={{
					marginBottom: 16,
					display: "flex",
					gap: 8,
					alignItems: "center",
				}}
			>
				<Button
					text={editMode ? "Готово" : "Редактировать"}
					onClick={() => setEditMode((v) => !v)}
				/>

				{editMode && (
					<>
						<select
							value={selectedUnitId}
							onChange={(e) => setSelectedUnitId(e.target.value)}
						>
							<option value="">Выберите юнит...</option>
							{availableUnits.map((u) => (
								<option key={u.id} value={u.id}>
									{u.isDiagram ? "Схема" : "Блок"}_{u.name}
								</option>
							))}
						</select>
						<Button
							onClick={handleAddWidget}
							disabled={!selectedUnitId}
							text="Добавить виджет"
						/>
					</>
				)}
			</div>

			<ResponsiveGridLayout
				className="layout"
				layouts={{ lg: layout }}
				breakpoints={BREAKPOINTS}
				cols={COLS}
				rowHeight={ROW_HEIGHT}
				isDraggable={editMode}
				isResizable={editMode}
				onDragStop={handleLayoutSettled}
				onResizeStop={handleLayoutSettled}
				draggableHandle=".page-widget-drag-handle"
			>
				{(page.widgets ?? []).map((widget) => (
					<div
						key={widget.id}
						style={{
							border: "1px solid #dee2e6",
							background: "#fff",
							overflow: "hidden",
							position: "relative",
						}}
					>
						{editMode && (
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									padding: "2px 6px",
									background: "#f1f3f5",
									cursor: "move",
								}}
								className="page-widget-drag-handle"
							>
								<span style={{ fontSize: 12, color: "#868e96" }}>
									{widget.unit?.isDiagram ? "Схема" : "Блок"}_
									{widget.unit?.name}
								</span>
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveWidget(widget.id);
									}}
									style={{ border: "none", background: "none" }}
									title="Удалить виджет со страницы"
									onMouseDown={(e) => e.stopPropagation()}
								>
									✕
								</button>
							</div>
						)}
						<div
							style={{
								width: "100%",
								height: editMode ? "calc(100% - 24px)" : "100%",
								pointerEvents: editMode ? "none" : "auto",
							}}
						>
							<WidgetRenderer widget={widget} />
						</div>
					</div>
				))}
			</ResponsiveGridLayout>
		</Content>
	);
}

export default PageCanvas;
